import { UnstyledButton, UnstyledButtonProps } from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";
import { modals } from "@mantine/modals";
import { ForwardedRef, PropsWithChildren, forwardRef } from "react";
import { ObjectSchema } from "yup";
type TModalName = Parameters<typeof modals.openContextModal>[0]["modal"];
// prettier-ignore
type EditItemNewButtonProperties<T extends object> = PropsWithChildren<
  UnstyledButtonProps & { onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void; item?: T; schema: ObjectSchema<T>; modal: TModalName, onSubmit?: (values: T, form: UseFormReturnType<T>) => void; }
>;

/**
 * A button that opens a modal to edit an item.
 * @param root0 - The properties of the button.
 * @param root0.item - The item to be edited.
 * @param root0.onSubmit - onsubmit
 * @param root0.modal - The modal to be opened.
 * @param root0.schema - The schema of the item.
 * @param root0.children - The children of the button.
 * @param reference - The reference to the button.
 * @returns The element to be rendered.
 */
function EditItemNewButton<T extends object>(
  { item, onSubmit, modal, schema, children, ...properties }: EditItemNewButtonProperties<T>,
  reference: ForwardedRef<HTMLButtonElement>
) {
  /**
   * Handles the click event on the button.
   * @param event - The event that triggered the click.
   */
  function handleClick(event: React.MouseEvent<HTMLButtonElement>) {
    const defaultValues = schema.getDefault() as T;
    event.preventDefault();

    if (properties.onClick) {
      properties.onClick(event);
    }

    modals.openContextModal({
      modal,
      size: "xl",
      closeOnClickOutside: false,
      closeOnEscape: false,
      innerProps: {
        item: {
          ...defaultValues,
          ...item
        },
        onSubmit
      },
      ml: "110px"
    });
  }

  return (
    <UnstyledButton ref={reference} {...properties} onClick={handleClick}>
      {children}
    </UnstyledButton>
  );
}

const EditItemNewButtonWithReference = forwardRef(EditItemNewButton);

EditItemNewButtonWithReference.displayName = "EditItemNewButton";

// export default createPolymorphicComponent(EditItemNewButtonWithReference);
export default EditItemNewButtonWithReference;
